import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../store/useStore';
import { formatNumber } from '../utils/formatters';
import BurnHistoryModal from './BurnHistoryModal';
import api from '../services/api';
import './BurnWidget.css';

interface BurnData {
    accumulated: number;
    nextBurnDate: string;
    progressPercent: number;
}

interface LastBurnData {
    showBanner: boolean;
    burn: {
        amount: number;
        transaction_hash: string;
        burn_date: string;
        days_since_burn: number;
    } | null;
}

const BurnWidget: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useStore();
    const [burnData, setBurnData] = useState<BurnData>({
        accumulated: 0,
        nextBurnDate: '',
        progressPercent: 0
    });
    const [lastBurn, setLastBurn] = useState<LastBurnData | null>(null);
    const [timeUntilBurn, setTimeUntilBurn] = useState('');
    const [burnHistoryModalOpen, setBurnHistoryModalOpen] = useState(false);

    useEffect(() => {
        // Загружаем данные о накопленном burn
        fetchBurnData();
        fetchLastBurn();

        // Обновляем каждые 10 секунд
        const interval = setInterval(() => {
            fetchBurnData();
            fetchLastBurn();
        }, 10000);
        return () => clearInterval(interval);
    }, []);

    // Определяем: сегодня день сжигания и оно ещё не выполнено?
    const isBurnDay = burnData.progressPercent >= 100;

    useEffect(() => {
        // Таймер использует nextBurnDate из бэкенда (burnData)
        const updateTimer = () => {
            // Если сжигание сегодня — таймер не нужен
            if (isBurnDay) {
                setTimeUntilBurn('');
                return;
            }

            if (!burnData.nextBurnDate) {
                setTimeUntilBurn('...');
                return;
            }

            const now = new Date();
            const nextBurnDate = new Date(burnData.nextBurnDate);
            const timeLeft = nextBurnDate.getTime() - now.getTime();

            if (timeLeft <= 0) {
                setTimeUntilBurn(t('burn.soon'));
                return;
            }

            const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
            const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

            setTimeUntilBurn(t('burn.timeFormat', { days, hours }));
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [burnData.nextBurnDate, burnData.progressPercent, isBurnDay]);

    const fetchBurnData = async () => {
        try {
            const response = await api.get('/burn/status');
            if (response.data.success) {
                setBurnData(response.data.data);
            }
        } catch (error) {
            console.error('Ошибка загрузки данных burn:', error);
            // При ошибке устанавливаем дефолтные значения
            const now = new Date();
            const currentDay = now.getDate();
            const progress = currentDay >= 10 ? 100 : Math.min(100, (currentDay / 10) * 100);

            setBurnData({
                accumulated: 0,
                nextBurnDate: new Date(now.getFullYear(), now.getMonth() + (currentDay >= 10 ? 1 : 0), 10).toISOString(),
                progressPercent: progress
            });
        }
    };

    const fetchLastBurn = async () => {
        try {
            const response = await api.get('/burn/last-burn');
            if (response.data.success) {
                setLastBurn(response.data.data);
            }
        } catch (error) {
            console.error('Ошибка загрузки последнего сжигания:', error);
            setLastBurn({ showBanner: false, burn: null });
        }
    };

    const handleClick = () => {
        console.log('🔥 BurnWidget clicked!', {
            telegramId: user?.telegramId
        });

        // Любой пользователь может открыть историю сжигания
        console.log('✅ Открываем модалку истории сжигания');
        setBurnHistoryModalOpen(true);
    };

    // Создаем прогресс-бар визуально
    const progressBarWidth = Math.min(100, burnData.progressPercent);
    const filledBlocks = Math.floor((progressBarWidth / 100) * 10); // 10 блоков всего
    const progressBar = '█'.repeat(filledBlocks) + '░'.repeat(10 - filledBlocks);

    return (
        <>
            <div
                className="burn-widget clickable"
                onClick={handleClick}
                onTouchEnd={handleClick}
                role="button"
                aria-label={t('burn.viewHistory')}
            >
                <div className="burn-content">
                    <>
                        <span className="burn-icon">🔥</span>
                        {isBurnDay ? (
                            <span className="burn-timer burn-today">{t('burn.today')}</span>
                        ) : (
                            <span className="burn-timer">{t('burn.burnIn')} {timeUntilBurn}</span>
                        )}
                        <span className="burn-separator">•</span>
                        <span className="burn-amount">{formatNumber(burnData.accumulated, 0)} SPERM</span>
                        <span className="burn-progress-bar">[{progressBar}]</span>
                        <span className="burn-percent">{Math.round(burnData.progressPercent)}%</span>
                    </>
                </div>
            </div>

            {/* Модальное окно истории burn */}
            <BurnHistoryModal
                isOpen={burnHistoryModalOpen}
                onClose={() => setBurnHistoryModalOpen(false)}
            />
        </>
    );
};

export default BurnWidget;
